import type { AdobeDispatcher } from '../../services/AdobeCollectionAPI/AdobeCollectionAPI';
import { trackPluginCall } from '../../util/overseer/overseer';
import type { UnifiedTrackingMachineEvent } from '../tracking/UnifiedTrackingMachine';
import type { UseTrackingMappedMedia } from '../../plugins/Tracking';

export interface AdobeGateway {
  onMedia(media: UseTrackingMappedMedia): void;

  trackEvent(tag: string, value?: any): void;

  trackSessionEnd(): void;

  trackComplete(): void;

  updatePlayhead(value: number): void;

  updateQoEObject(qoe: any): void;

  trackSessionStart(mediaObject: any, mediaMetadata: any): void;

  setSource(event: UnifiedTrackingMachineEvent): void;
}

export class AggregateAdobeGateway implements AdobeGateway {
  private adobeGateways: AdobeGateway[];

  constructor(adobeGateways: AdobeGateway[]) {
    this.adobeGateways = adobeGateways;
  }

  onMedia(media: UseTrackingMappedMedia): void {
    this.adobeGateways.forEach(gateway => gateway.onMedia(media));
  }

  trackEvent(tag: string, value?: any): void {
    this.adobeGateways.forEach(gateway => gateway.trackEvent(tag, value));
  }

  trackSessionEnd(): void {
    this.adobeGateways.forEach(gateway => gateway.trackSessionEnd());
  }

  trackComplete(): void {
    this.adobeGateways.forEach(gateway => gateway.trackComplete());
  }

  updatePlayhead(value: number): void {
    this.adobeGateways.forEach(gateway => gateway.updatePlayhead(value));
  }

  updateQoEObject(qoe: any): void {
    this.adobeGateways.forEach(gateway => gateway.updateQoEObject(qoe));
  }

  trackSessionStart(mediaObject: any, mediaMetadata: any): void {
    this.adobeGateways.forEach(gateway => gateway.trackSessionStart(mediaObject, mediaMetadata));
  }

  setSource(event: UnifiedTrackingMachineEvent): void {
    this.adobeGateways.forEach(gateway => gateway.setSource(event));
  }
}

export class AdobeOverseerGateway implements AdobeGateway {
  private instanceId: string;
  private overseerMetadata: any;
  private pluginName: string;
  private source: UnifiedTrackingMachineEvent | null;

  constructor(pluginName: string = 'Adobe Module') {
    this.instanceId = Math.random().toString(36).substring(2, 15);
    this.overseerMetadata = { instanceId: this.instanceId };
    this.pluginName = pluginName;
  }

  private track(event: string, value: any): void {
    trackPluginCall(this.pluginName, event, value, {
      ...this.overseerMetadata,
      source: this.source,
    });
  }

  trackEvent(tag: string, value: any): void {
    this.track(tag, value);
  }

  trackSessionEnd(): void {
    this.track('sessionEnd', null);
  }

  trackComplete(): void {
    this.track('complete', null);
  }

  updatePlayhead(value: number): void {
    this.track('playhead', value);
  }

  updateQoEObject(qoe: any): void {
    this.track('qoe', qoe);
  }

  trackSessionStart(mediaObject: any, mediaMetadata: any): void {
    this.track('sessionStart', { mediaObject, mediaMetadata });
  }

  setSource(event: UnifiedTrackingMachineEvent): void {
    this.source = event;
  }

  onMedia(_media: UseTrackingMappedMedia): void {}
}

type DispatcherFactory = (media: UseTrackingMappedMedia) => AdobeDispatcher;

export class AdobeModuleGateway implements AdobeGateway {
  private readonly dispatcherFactory: DispatcherFactory;
  private dispatcher: AdobeDispatcher;

  constructor(dispatcherOrFactory: DispatcherFactory | AdobeDispatcher) {
    if (typeof dispatcherOrFactory === 'function') {
      this.dispatcherFactory = dispatcherOrFactory;
    } else {
      this.dispatcher = dispatcherOrFactory;
    }
  }

  onMedia(media: UseTrackingMappedMedia): void {
    this.dispatcher = this.dispatcherFactory(media);
  }

  trackEvent(tag: string, value: any): void {
    this.dispatcher.trackEvent(tag, value);
  }

  trackSessionEnd(): void {
    this.dispatcher.trackSessionEnd();
  }

  trackComplete(): void {
    this.dispatcher.trackComplete();
  }

  updatePlayhead(value: number): void {
    this.dispatcher.updatePlayhead(value);
  }

  updateQoEObject(qoe: any): void {
    this.dispatcher.updateQoEObject(qoe);
  }

  trackSessionStart(mediaObject: any, mediaMetadata: any): void {
    this.dispatcher.trackSessionStart(mediaObject, mediaMetadata);
  }

  setSource(_event: UnifiedTrackingMachineEvent): void {
    // Not used
  }
}
